import { useCallback, useMemo, useRef, useState } from 'react';

import { GearbeltLoader } from '@gearbelt/ui/icons';
import { Arrow as TooltipArrow } from '@radix-ui/react-tooltip';
import { Products } from '@site-mate/sitemate-global-shared';
import { WorkspaceUrlService } from '@site-mate/sitemate-infra-shared/services';

import sitemateStartTooltipIllustration from '@/assets/workspace-switcher/sitemate-start-tooltip-illustration.svg';
import PlusIcon from '@/components/icons/plus';
import QuestionIcon from '@/components/icons/question';
import SwitchIcon from '@/components/icons/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useWorkspaceSwitcherStore } from '@/stores/workspace-switcher-store';

import {
  generateWorkspaceUrl,
  getAvatarColor,
  getCreateWorkspaceUrl,
  getInitial,
  IAccountCreatePermissions,
  IAccountGroup,
  IProductCreatePermission,
  IWorkspaceInfo,
  PRODUCT_ICONS,
  PRODUCT_ORDER,
} from './workspace-switcher-menu.utils';

function TruncatedText({ text, className }: { text: string; className?: string }) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number }>({ visible: false, x: 0, y: 0 });

  const handleMouseEnter = useCallback((e: React.MouseEvent) => {
    const el = textRef.current;
    if (el && el.scrollWidth > el.clientWidth) {
      setTooltip({ visible: true, x: e.clientX, y: e.clientY });
    }
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (tooltip.visible) {
        setTooltip({ visible: true, x: e.clientX, y: e.clientY });
      }
    },
    [tooltip.visible],
  );

  const handleMouseLeave = useCallback(() => {
    setTooltip({ visible: false, x: 0, y: 0 });
  }, []);

  return (
    <>
      <span ref={textRef} className={className} onMouseEnter={handleMouseEnter} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
        {text}
      </span>
      {tooltip.visible && (
        <div className="fixed z-[10000] rounded px-2 py-1 text-sm text-white" style={{ backgroundColor: '#3b4a54', left: tooltip.x + 8, top: tooltip.y + 16 }}>
          {text}
        </div>
      )}
    </>
  );
}

function AccountHeader({ accountName, isSuperUser, onSwitchAccount }: { accountName: string; isSuperUser: boolean; onSwitchAccount?: () => void }) {
  return (
    <div className="flex items-center justify-between px-3 pt-3">
      <div className="flex min-w-0 items-center gap-1">
        <div
          className="flex h-5 w-5 shrink-0 items-center justify-center text-xs font-bold text-white"
          style={{ backgroundColor: getAvatarColor(accountName), borderRadius: '2px' }}>
          {getInitial(accountName)}
        </div>
        <TruncatedText text={accountName} className="text-default-text truncate text-sm font-bold" />
      </div>

      {isSuperUser && (
        <button
          className="text-default-text hover:bg-grey-1 flex h-5 w-5 shrink-0 items-center justify-center rounded"
          onClick={onSwitchAccount}
          aria-label="Switch to another Account">
          <SwitchIcon size={20} />
        </button>
      )}
    </div>
  );
}

function ProductHeader({
  product,
  createPermission,
  onCreateWorkspace,
  onUpgradeAction,
}: {
  product: Products;
  createPermission?: IProductCreatePermission;
  onCreateWorkspace?: () => void;
  onUpgradeAction?: () => void;
}) {
  const icon = PRODUCT_ICONS[product];
  const hasCreatePermission = createPermission?.canCreate && onCreateWorkspace;
  const isPlanRestricted = createPermission?.isDisabled && createPermission.disabledMessage;

  const createButton = (
    <button
      onClick={onCreateWorkspace}
      disabled={createPermission?.isDisabled}
      className="text-default-text hover:bg-grey-1 flex h-5 w-5 items-center justify-center rounded disabled:cursor-not-allowed disabled:opacity-50"
      aria-label={`Create ${product} workspace`}>
      <PlusIcon size={16} />
    </button>
  );

  return (
    <div className="flex items-center justify-between px-3 pt-3 pb-1">
      <div className="flex items-center gap-1">
        {icon && <img src={icon} alt={product} className="h-5 w-5" />}
        <span className="text-default-text text-sm">{product}</span>
      </div>

      {hasCreatePermission &&
        (isPlanRestricted ? (
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span>{createButton}</span>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-[260px]">
                <TooltipArrow className="fill-white drop-shadow-[0_1px_0_rgb(var(--grey-3))]" />
                <span className="text-sm">
                  {createPermission.disabledMessage}.{' '}
                  <button type="button" className="text-default-blue inline cursor-pointer underline" onClick={onUpgradeAction}>
                    Upgrade your plan
                  </button>{' '}
                  to unlock this feature.
                </span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          createButton
        ))}
    </div>
  );
}

function WorkspaceItem({ workspace }: { workspace: IWorkspaceInfo }) {
  return (
    <a
      href={generateWorkspaceUrl(workspace)}
      className="hover:bg-grey-1 flex items-center gap-2 rounded px-3 py-1.5"
      role="menuitem"
      aria-label={workspace.name}>
      <div className="ml-2 flex items-center gap-1">
        {workspace.logo ? (
          <img src={workspace.logo} alt={workspace.name} className="h-5 w-5 rounded-sm object-contain" />
        ) : (
          <div
            className="flex h-5 w-5 shrink-0 items-center justify-center text-xs font-bold text-white"
            style={{ backgroundColor: getAvatarColor(workspace.name), borderRadius: '2px' }}>
            {getInitial(workspace.name)}
          </div>
        )}
        <TruncatedText text={workspace.name} className="text-default-text max-w-[200px] truncate text-sm" />
      </div>
    </a>
  );
}

function SitemateStartTooltip() {
  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <span className="text-default-text hover:bg-grey-1 flex h-5 w-5 items-center justify-center rounded" aria-label="Learn more about Sitemate Start">
            <QuestionIcon size={16} />
          </span>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[284px] p-4" collisionPadding={16}>
          <TooltipArrow className="fill-white drop-shadow-[0_1px_0_rgb(var(--grey-3))]" />
          <div className="text-default-text flex flex-col gap-3 text-sm">
            <img src={sitemateStartTooltipIllustration} alt="Sitemate Start features" className="w-full" />
            <p>
              <span className="font-bold">Sitemate Start</span> is a central account layer that lets you manage users, templates, and lists across all your
              workspaces in one place.
            </p>
            <div>
              <p className="font-medium">Common use cases:</p>
              <ul className="mt-1 flex flex-col gap-0.5 pl-4">
                <li className="list-disc">View all Users across multiple workspaces</li>
                <li className="list-disc">Use Templates across multiple workspaces</li>
                <li className="list-disc">Use Lists across multiple workspaces</li>
              </ul>
            </div>
            <p>Please contact your Sitemate representative or use the live chat to learn more.</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface SitemateStartSectionProps {
  isAccountLayerEnabled: boolean;
  hasAccess: boolean;
  isSuperUser: boolean;
  accountName: string;
  isPending: boolean;
  onCreateSmsAccount: () => void;
  onSmsAccountSelected: () => void;
}

function SitemateStartSection({
  isAccountLayerEnabled,
  hasAccess,
  isSuperUser,
  accountName,
  isPending,
  onCreateSmsAccount,
  onSmsAccountSelected,
}: SitemateStartSectionProps) {
  const icon = PRODUCT_ICONS[Products.SitemateStart];
  const showCreateButton = !isAccountLayerEnabled && isSuperUser;
  const showWorkspaceRow = isAccountLayerEnabled && hasAccess;
  const showTooltip = !showCreateButton && !showWorkspaceRow;

  return (
    <div>
      <div className="flex items-center justify-between px-3 pt-3 pb-1">
        <div className="flex items-center gap-1">
          {icon && <img src={icon} alt="Sitemate Start" className="h-5 w-5" />}
          <span className="text-default-text text-sm">{Products.SitemateStart}</span>
        </div>

        {showCreateButton &&
          (isPending ? (
            <div className="flex h-5 w-5 items-center justify-center">
              <span className="border-default-blue size-4 animate-spin rounded-full border-2 border-t-transparent" />
            </div>
          ) : (
            <button
              onClick={onCreateSmsAccount}
              className="text-default-text hover:bg-grey-1 flex h-5 w-5 items-center justify-center rounded"
              aria-label="Create Sitemate Start account">
              <PlusIcon size={16} />
            </button>
          ))}

        {showTooltip && <SitemateStartTooltip />}
      </div>

      {showWorkspaceRow && (
        <button
          onClick={onSmsAccountSelected}
          className="hover:bg-grey-1 flex w-full items-center gap-2 rounded px-3 py-1.5"
          role="menuitem"
          aria-label={accountName}>
          <div className="ml-2 flex items-center gap-1">
            <div
              className="flex h-5 w-5 shrink-0 items-center justify-center text-xs font-bold text-white"
              style={{ backgroundColor: getAvatarColor(accountName), borderRadius: '2px' }}>
              {getInitial(accountName)}
            </div>
            <TruncatedText text={accountName} className="text-default-text max-w-[200px] truncate text-sm" />
          </div>
        </button>
      )}
    </div>
  );
}

function AccountSection({
  account,
  isSuperUser,
  onSwitchAccount,
  accountPermissions,
  onCreateWorkspace,
  onClose,
  isAccountLayerEnabled,
  hasAccountLayerAccess,
  pendingAccountLayerId,
  onCreateSmsAccount,
  onSmsAccountSelected,
}: {
  account: IAccountGroup;
  isSuperUser: boolean;
  onSwitchAccount?: () => void;
  accountPermissions?: IAccountCreatePermissions;
  onCreateWorkspace?: (accountId: string, product: Products) => void;
  onClose?: () => void;
  isAccountLayerEnabled?: boolean;
  hasAccountLayerAccess?: boolean;
  pendingAccountLayerId?: string | null;
  onCreateSmsAccount?: (accountId: string) => void;
  onSmsAccountSelected?: (accountId: string) => void;
}) {
  const productSections = useMemo(() => {
    if (isSuperUser) {
      return PRODUCT_ORDER.map((product) => {
        const existing = account.products.find((pg) => pg.name === product);
        return existing ?? { name: product, workspaces: [] };
      });
    }

    return PRODUCT_ORDER.map((product) => {
      const existing = account.products.find((pg) => pg.name === product);
      const hasWorkspaces = existing && existing.workspaces.length > 0;
      const perm = accountPermissions?.[product];
      const canCreate = perm?.canCreate ?? false;

      if (!hasWorkspaces && (!canCreate || perm?.isDisabled)) return null;

      return existing ?? { name: product, workspaces: [] };
    }).filter(Boolean) as { name: Products; workspaces: IWorkspaceInfo[] }[];
  }, [account.products, isSuperUser, accountPermissions]);

  const { setIsUpgradePlanOpen } = useWorkspaceSwitcherStore();

  const handleUpgradeAction = useCallback(
    (product: Products) => {
      onClose?.();
      setIsUpgradePlanOpen(true, product);
    },
    [onClose, setIsUpgradePlanOpen],
  );

  return (
    <div className="flex flex-col">
      <AccountHeader accountName={account.accountName} isSuperUser={isSuperUser} onSwitchAccount={onSwitchAccount} />

      <SitemateStartSection
        isAccountLayerEnabled={isAccountLayerEnabled ?? false}
        hasAccess={hasAccountLayerAccess ?? false}
        isSuperUser={isSuperUser}
        accountName={account.accountName}
        isPending={pendingAccountLayerId === account.accountId}
        onCreateSmsAccount={() => onCreateSmsAccount?.(account.accountId)}
        onSmsAccountSelected={() => onSmsAccountSelected?.(account.accountId)}
      />

      {productSections.map((productGroup) => {
        const perm = accountPermissions?.[productGroup.name];

        return (
          <div key={productGroup.name}>
            <ProductHeader
              product={productGroup.name}
              createPermission={perm}
              onCreateWorkspace={onCreateWorkspace ? () => onCreateWorkspace(account.accountId, productGroup.name) : undefined}
              onUpgradeAction={() => handleUpgradeAction(productGroup.name)}
            />
            <div className="flex flex-col">
              {productGroup.workspaces.map((workspace) => (
                <WorkspaceItem key={workspace.id} workspace={workspace} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface WorkspaceSwitcherMenuProps {
  accounts: IAccountGroup[];
  isSuperUser: boolean;
  currentAccountId?: string | null;
  isLoading: boolean;
  isError: boolean;
  onSwitchAccount?: () => void;
  permissionsByAccount?: Map<string, IAccountCreatePermissions>;
  onCreateGearbeltWorkspace?: (accountId: string) => void;
  onClose?: () => void;
  accountLayerEnabledByAccount?: Map<string, boolean>;
  accountLayerAccessByAccount?: Map<string, boolean>;
  pendingAccountLayerId?: string | null;
  onCreateSmsAccount?: (accountId: string) => void;
  onSmsAccountSelected?: (accountId: string) => void;
}

export default function WorkspaceSwitcherMenu({
  accounts,
  isSuperUser,
  currentAccountId,
  isLoading,
  isError,
  onSwitchAccount,
  permissionsByAccount,
  onCreateGearbeltWorkspace,
  onClose,
  accountLayerEnabledByAccount,
  accountLayerAccessByAccount,
  pendingAccountLayerId,
  onCreateSmsAccount,
  onSmsAccountSelected,
}: WorkspaceSwitcherMenuProps) {
  const workspaceUrlService = useMemo(() => new WorkspaceUrlService(), []);
  const displayedAccounts = useMemo(() => {
    if (isSuperUser && currentAccountId) {
      return accounts.filter((account) => account.accountId === currentAccountId);
    }

    if (!currentAccountId) return accounts;

    const currentAccount = accounts.find((account) => account.accountId === currentAccountId);
    const otherAccounts = accounts.filter((account) => account.accountId !== currentAccountId);
    return currentAccount ? [currentAccount, ...otherAccounts] : accounts;
  }, [accounts, isSuperUser, currentAccountId]);

  const handleCreateWorkspace = useCallback(
    (accountId: string, product: Products) => {
      if (product === Products.Gearbelt && onCreateGearbeltWorkspace) {
        onCreateGearbeltWorkspace(accountId);
        return;
      }

      const account = accounts.find((a) => a.accountId === accountId);
      const anyWorkspace = account?.products.flatMap((p) => p.workspaces)[0];

      const url = getCreateWorkspaceUrl(product, accountId, workspaceUrlService, window.location, {
        region: anyWorkspace?.region,
        subdomain: anyWorkspace?.subdomain,
      });
      window.location.href = url;
    },
    [accounts, workspaceUrlService, onCreateGearbeltWorkspace],
  );

  return (
    <div
      className="flex w-[300px] flex-col rounded-[5px] bg-white"
      role="menu"
      style={{ maxHeight: 'calc(100vh - 87px - 12px)', boxShadow: '0px 5px 10px 0px rgba(0, 0, 0, 0.2)' }}>
      {isLoading && (
        <div className="flex min-h-[200px] items-center justify-center">
          <GearbeltLoader size={30} />
        </div>
      )}

      {isError && (
        <div className="flex min-h-[200px] items-center justify-center">
          <span className="text-secondary-text text-sm">Unable to load workspaces. Please try again.</span>
        </div>
      )}

      {!isLoading && !isError && (
        <>
          <div className={`overflow-y-auto ${isSuperUser ? 'pb-2.5' : ''}`}>
            {displayedAccounts.map((account, index) => (
              <div key={account.accountId}>
                {index > 0 && <div className="bg-grey-2 mx-[10px] h-[2px] rounded-sm" />}
                <AccountSection
                  account={account}
                  isSuperUser={isSuperUser}
                  onSwitchAccount={onSwitchAccount}
                  accountPermissions={permissionsByAccount?.get(account.accountId)}
                  onCreateWorkspace={handleCreateWorkspace}
                  onClose={onClose}
                  isAccountLayerEnabled={accountLayerEnabledByAccount?.get(account.accountId)}
                  hasAccountLayerAccess={accountLayerAccessByAccount?.get(account.accountId)}
                  pendingAccountLayerId={pendingAccountLayerId}
                  onCreateSmsAccount={onCreateSmsAccount}
                  onSmsAccountSelected={onSmsAccountSelected}
                />
              </div>
            ))}
          </div>

          {!isSuperUser && (
            <div className="shrink-0 px-[10px] pb-2">
              <div className="bg-grey-2 mb-1.5 h-[2px] rounded-sm" />
              <span className="text-secondary-text text-sm">Only users with Org Controller permissions can create additional workspaces.</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
